import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { parseCoordinate } from '../../proposalLayout/diffHelpers/diffHelpers';
import {
  UseChangedSubgraphHashesFirstLaunchFragment,
  useChangedSubgraphHashes,
} from '../../proposalLayout/diffHelpers/useChangedSubgraphHashes';
import { useMultipleDiffs } from '../../proposalLayout/diffHelpers/useMultipleDiffs';
import { ProposalBuildInputFragment } from '../../proposalLayout/proposalEditor/shared';
import { ReferenceCategorySlug } from '../../routes';
import { CoordinateChangedLookup } from '../schemaPage/schemaFilter/filterSchema';

export const SupergraphAndApiSchemaChangesFragment = gql`
  fragment SupergraphAndApiSchemaChangesFragment on GraphVariant {
    id
    activeSchemaPublish {
      id
      schema {
        createdAt
        hash
        document
      }
      # TODO: add back supergraph fetching once permission bug is fixed
      launch @skip(if: $shouldSkipFetchingLaunch) {
        id
        build {
          result {
            ... on BuildSuccess {
              coreSchema {
                coreHash
              }
            }
          }
        }
      }
    }
  }
`;

export const UseRemovedCoordinatesLookupGraphFragment = gql`
  fragment UseRemovedCoordinatesLookupGraphFragment on Service {
    id
    variant(name: $graphVariant) {
      id
      firstLaunch: launchHistory(limit: 1, orderBy: CREATED_ASC) {
        id
        ...UseChangedSubgraphHashesFirstLaunchFragment
      }

      activeSchemaPublish {
        id
        history(limit: 10, offset: $historyOffset) {
          id
          schema {
            createdAt
            hash
            document
          }
          # TODO: add back supergraph fetching once permission bug is fixed
          launch @skip(if: $shouldSkipFetchingLaunch) {
            id
            build {
              result {
                ... on BuildSuccess {
                  coreSchema {
                    coreHash
                  }
                }
              }
            }
          }
        }
      }
      ...ProposalBuildInputFragment
      ...SupergraphAndApiSchemaChangesFragment
    }
  }
  ${UseChangedSubgraphHashesFirstLaunchFragment}
  ${ProposalBuildInputFragment}
  ${SupergraphAndApiSchemaChangesFragment}
`;

interface RemovedLookup {
  fields: Record<
    string,
    Record<string, { value?: string; subgraphs: string[] }>
  >;
  types: {
    [key in ReferenceCategorySlug]?: Record<string, { subgraphs: string[] }>;
  };
}
function setTypeRemoved({
  removedLookup,
  coordinate,
  kind,
  subgraph,
}: {
  removedLookup: RemovedLookup;
  coordinate: string;
  kind: ReferenceCategorySlug;
  subgraph: string;
}) {
  const typeLookup = getOrSetRecord(kind, {}, removedLookup.types);
  getOrSetRecord(coordinate, { subgraphs: [] }, typeLookup).subgraphs.push(
    subgraph,
  );
  // eslint-disable-next-line no-param-reassign
  delete removedLookup.fields[coordinate];
}
export function getRemovedCoordinatesSubgraphMap(
  subgraphsData: ReturnType<typeof useMultipleDiffs>['data'],
) {
  return Object.entries(subgraphsData).reduce(
    (acc: RemovedLookup, [subgraph, subgraphDiff]) => {
      const flatDiffResult = subgraphDiff?.data?.service?.flatDiff;
      const flatDiff =
        flatDiffResult?.__typename === 'FlatDiff' ? flatDiffResult.diff : null;
      flatDiff?.forEach((diff) => {
        switch (diff.__typename) {
          case 'FlatDiffRemoveObject':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'objects',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveScalar':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'scalars',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveInterface':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'interfaces',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveUnion':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'unions',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveEnum':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'enums',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveInput':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'inputs',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveDirective':
            setTypeRemoved({
              coordinate: diff.coordinate,
              kind: 'directives',
              removedLookup: acc,
              subgraph,
            });
            break;
          case 'FlatDiffRemoveEnumValue':
          case 'FlatDiffRemoveField': {
            const coordinate = parseCoordinate(diff);
            if ('type' in coordinate) {
              const fieldsLookup = getOrSetRecord(
                coordinate.type,
                {},
                acc.fields,
              );
              const fieldData = getOrSetRecord(
                coordinate.field ?? '',
                { subgraphs: [] },
                fieldsLookup,
              );
              fieldData.subgraphs.push(subgraph);
              if (diff.__typename === 'FlatDiffRemoveField') {
                fieldData.value = diff.value;
              }
            }
            break;
          }
          case 'FlatDiffRemoveUnionMember': {
            const fieldsLookup = getOrSetRecord(
              diff.coordinate,
              {},
              acc.fields,
            );
            getOrSetRecord(
              diff.value,
              { subgraphs: [] },
              fieldsLookup,
            ).subgraphs.push(subgraph);
            break;
          }
          default:
          // nothing to do for non removals
        }
      });
      return acc;
    },
    { fields: {}, types: {} },
  );
}
export function useRemovedCoordinatesLookup(
  graphId: string | undefined,
  graph:
    | GraphQLTypes.UseRemovedCoordinatesLookupGraphFragment
    | undefined
    | null,
) {
  const changedSubgraphHashes = useChangedSubgraphHashes(
    graph?.variant?.latestLaunch,
    graph?.variant?.firstLaunch?.[0],
    graph?.variant?.activeSchemaPublish,
  );

  const subgraphDiffs = useMultipleDiffs(graphId, changedSubgraphHashes);

  const removedCoordinateSubgraphMap = useMemo(
    () => getRemovedCoordinatesSubgraphMap(subgraphDiffs.data),
    [subgraphDiffs.data],
  );

  const changedCoordinates = useMemo(() => {
    const diffItems = Object.values(subgraphDiffs.data).flatMap(
      (diffResult) => {
        const flatDiffResult = diffResult?.data?.service?.flatDiff;
        return flatDiffResult?.__typename === 'FlatDiff'
          ? flatDiffResult.diff
          : [];
      },
    );

    return diffItems.reduce(
      (acc: CoordinateChangedLookup, diffItem) => {
        const coordinate = parseCoordinate(diffItem);
        if ('type' in coordinate) {
          const type = getOrSetRecord(coordinate.type, {}, acc.types);
          if (coordinate.field) {
            type[coordinate.field] = true;
          }
        } else if ('directive' in coordinate) {
          acc.directives[coordinate.directive] = true;
        }
        return acc;
      },
      { types: {}, directives: {} },
    );
  }, [subgraphDiffs.data]);
  return { ...subgraphDiffs, removedCoordinateSubgraphMap, changedCoordinates };
}
